import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { byPreferredUnitOrder, isAvailable } from "./dispatchUnitUtils";
import { getSuggestedData } from "./dispatchCallUtils";

export function useDispatchData({ callType, location } = {}) {
  const [userReady, setUserReady] = useState(false);
  const [baseConfig, setBaseConfig] = useState(null);
  const [assignmentsConfig, setAssignmentsConfig] = useState(null);
  const [units, setUnits] = useState([]);
  const [calls, setCalls] = useState([]);
  const [debug, setDebug] = useState([]);
  const [nowMs, setNowMs] = useState(Date.now());

  function pushDebug(msg) {
    setDebug((prev) => [...prev, msg]);
  }

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setUserReady(true);
      setDebug([]);

      if (!user) {
        pushDebug("Not signed in.");
        return;
      }

      pushDebug(`Signed in as ${user.email || user.uid}`);
    });

    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!userReady) return;

    const unsubBaseConfig = onSnapshot(
      doc(db, "dispatchConfig", "current"),
      (snap) => {
        if (!snap.exists()) {
          setBaseConfig(null);
          pushDebug("dispatchConfig/current missing.");
          return;
        }

        setBaseConfig(snap.data());
      },
      (err) => {
        console.error("dispatchConfig/current subscribe failed:", err);
        pushDebug(`Config subscribe failed: ${err.message}`);
      }
    );

    const unsubAssignmentsConfig = onSnapshot(
      doc(db, "dispatchConfig", "responseAssignments"),
      (snap) => {
        if (!snap.exists()) {
          setAssignmentsConfig(null);
          pushDebug("dispatchConfig/responseAssignments missing.");
          return;
        }

        setAssignmentsConfig(snap.data());
      },
      (err) => {
        console.error("dispatchConfig/responseAssignments subscribe failed:", err);
        pushDebug(`Assignments subscribe failed: ${err.message}`);
      }
    );

    const unsubUnits = onSnapshot(
      collection(db, "dispatchUnits"),
      (snap) => {
        setUnits(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort(byPreferredUnitOrder)
        );
      },
      (err) => {
        console.error("dispatchUnits subscribe failed:", err);
        pushDebug(`Units subscribe failed: ${err.message}`);
      }
    );

    const unsubCalls = onSnapshot(
      collection(db, "dispatchCalls"),
      (snap) => {
        setCalls(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("dispatchCalls subscribe failed:", err);
        pushDebug(`Calls subscribe failed: ${err.message}`);
      }
    );

    return () => {
      unsubBaseConfig();
      unsubAssignmentsConfig();
      unsubUnits();
      unsubCalls();
    };
  }, [userReady]);

  const config = useMemo(() => {
    return {
      ...(baseConfig || {}),
      responseAssignments: assignmentsConfig?.locations || {},
    };
  }, [baseConfig, assignmentsConfig]);

  const availableUnits = useMemo(
    () => units.filter((u) => isAvailable(u)).sort(byPreferredUnitOrder),
    [units]
  );

  const suggested = useMemo(
    () =>
      getSuggestedData({
        config,
        location,
        callType,
        units,
        isAvailableFn: isAvailable,
        byPreferredUnitOrder,
      }),
    [config, location, callType, units]
  );

  return {
    userReady,
    config,
    units,
    calls,
    debug,
    nowMs,
    availableUnits,
    suggested,
  };
}